<template>
  <section class="position-relative pt-3 select-items px-3" v-if="!isLoading && !initialLoading">
      <b-row class="justify-content-md-center">
        <b-col lg="10" class="pl-sm-3 pr-sm-3 pl-0 pr-0">
          <div v-if="orders.length">
            <div class="orders-container" v-for="order in orders" :key="'order-' + order.id.toString()">
              <div class="top-container d-flex align-items-center">
                <span class="top-container-text">#{{order.order_id}}</span>
                <div class="separator">|</div>
                <span class="top-container-text">{{getShortHumanReadableDate(order.created)}}</span>
                <div class="separator">|</div>
                <span class="badge order-status" :class="getPaymentStatusClass(order)">{{getPaymentStatus(order)}}</span>
              </div>
              <div class="order-container" v-for="item in order.items" :key="'item-' + item.id.toString()" @click="toggleItem($event, item)">
                <div class="order-item-type d-flex flex-column card-padding">
                  <div>
                    <template v-if="isRecurring(item)">
                      <span class="mr-2">Subscription</span>
                      <span class="badge order-status" :class="getItemStatusClass(item)">
                        {{getItemStatus(item)}}
                      </span>
                      <template v-if="item.campaign_product.show_next_bill_price">
                        <template v-if="isSubscriptionActive(item)">
                          <div v-if="item.next_bill_date">
                            Next billing date :
                            <span class="fw-500 fs-16">{{getShortHumanReadableUTCDate(item.next_bill_date)}}</span>
                          </div>
                          <div v-if="isSubscriptionPauseScheduled(item)">
                            Subscription will then pause until <span class="fw-500 fs-16">{{getShortHumanReadableUTCDate(item.pause_resume_date)}}</span>
                          </div>
                          <div v-else-if="isSubscriptionCancelScheduled(item)">
                            Subscription will cancel on <span class="fw-500 fs-16">{{getShortHumanReadableUTCDate(item.cancel_date)}}</span>
                          </div>
                        </template>
                        <div v-else-if="isSubscriptionComplete(item)">
                          No future billing
                        </div>
                      </template>
                    </template>
                    <template v-else>
                      <span class="mr-2">One-time purchase</span>
                    </template>
                  </div>
                </div>
                <hr class="m-0" />
                <div class="order-item-container card-padding pl-0" :class="{selected: isSelected(item)}">
                  <div class="item-image">
                    <img :src="getImageUrl(item)" @error="setAltImg($event, item)" alt="" />
                  </div>
                  <div class="order-item-info">
                    <div>
                      <span class="item-name" v-html="item.name"></span>
                      <div v-if="item.variant" class="item-variants">
                        <div v-for="option in item.variant.options" :key="'variant-option-' + option.id">
                          <span v-if="!option.is_deleted">{{option.name}}: {{option.value}}</span>
                        </div>
                      </div>
                    </div>
                    <div v-if="item.quantity > 1 || showFulfillment(item)" class="item-quantity">
                      Qty: <span>{{item.quantity}}</span>
                    </div>
                    <div class="notranslate item-price">
                      <div :class="{'original-price': isReplaced(item)}">{{item.price | currency(order.currency)}}</div>
                    </div>
                    <div v-if="hasPendingCharge(item)">
                      Pending charge for {{item.amount_pending | currency(order.currency)}}
                    </div>
                    <div v-if="showFulfillment(item)">
                      <span class="item-status-icon">
                        <font-awesome-icon :class="getItemFulfillmentStatusClass(item)" :icon="['fas', 'circle']"></font-awesome-icon>
                        {{getItemFulfillmentStatus(item)}}
                      </span>
                    </div>
                  </div>
                  <b-button class="order-item-select-btn no-outline-btn position-relative d-none d-md-block" @click="toggleItem($event, item)">
                    <span v-show="isSelected(item)">Selected!</span>
                    <template v-show="!isSelected(item)">
                      <span class="flex-1" :class="{'mr-1': itemSelectIcon}">{{itemSelectLabel}}</span>
                      <font-awesome-icon class="position-absolute" v-if="itemSelectIcon" :icon="['fas', itemSelectIcon]"></font-awesome-icon>
                    </template>
                  </b-button>
                </div>
                <div v-if="!noselect" class="d-block d-md-none">
                  <hr class="m-0"/>
                  <div class="card-padding">
                    <b-button block class="order-item-select-btn no-outline-btn position-relative" @click="toggleItem($event, item)">
                      <span v-show="isSelected(item)">Selected!</span>
                      <template v-show="!isSelected(item)">
                        <span class="flex-1" :class="{'mr-1': itemSelectIcon}">{{itemSelectLabel}}</span>
                        <font-awesome-icon class="position-absolute" v-if="itemSelectIcon" :icon="['fas', itemSelectIcon]"></font-awesome-icon>
                      </template>
                    </b-button>
                  </div>
                </div>
                <div class="d-none campaign-product-id">{{item.campaign_product.campaign_product_id}}</div>
              </div>
              <div class="order-totals-info d-flex">
                <div>
                  <div class="order-totals-info-item">
                    <span class="total-heading">Subtotal:</span>
                    <span class="notranslate total-text">{{order.subtotal | currency(order.currency)}}</span>
                  </div>
                  <div class="order-totals-info-item" v-if="showShipping(order)">
                    <span class="total-heading">Shipping:</span>
                    <span class="notranslate total-text">{{order.shipping_price | currency(order.currency)}}</span>
                  </div>
                  <div class="order-totals-info-item" v-if="showHiddenFees(order)">
                    <span class="total-heading">{{session.hidden_fees_label}}:</span>
                    <span class="notranslate total-text">{{order.hidden_fees | currency(order.currency)}}</span>
                  </div>
                  <div class="order-totals-info-item" v-if="showDiscount(order)">
                    <span class="total-heading">Discount:</span>
                    <span class="notranslate total-text cancelled">-{{order.discount | currency(order.currency)}}</span>
                  </div>
                  <div class="order-totals-info-item" v-if="Number(order.tax) > 0">
                    <span class="total-heading">Tax:</span>
                    <span class="notranslate total-text">{{order.tax | currency(order.currency)}}</span>
                  </div>
                  <div class="order-totals-info-item" v-if="Number(order.tip) > 0">
                    <span class="total-heading">Tip:</span>
                    <span class="notranslate total-text">{{order.tip | currency(order.currency)}}</span>
                  </div>
                  <hr class="mt-0 order-totals-info-item w-100" />
                  <div class="order-totals-info-item" >
                    <div class="label total-heading fw-500">Total:</div>
                    <div class="notranslate total-text">{{order.total | currency(order.currency)}}</div>
                  </div>
                </div>
                <div>
                  <div class="order-totals-info-item" v-if="showPastBalance(order)">
                    <div class="total-heading">Past Payments:</div>
                    <div class="notranslate total-text">{{order.past_balance | currency(order.currency)}}</div>
                  </div>
                  <div class="order-totals-info-item" v-if="showForwardBalance(order)">
                    <div class="total-heading">Carry Forward:</div>
                    <div class="notranslate total-text cancelled">-{{order.forward_balance | currency(order.currency)}}</div>
                  </div>
                  <div class="order-totals-info-item">
                    <div class="total-heading">Amount Paid:</div>
                    <div class="notranslate total-text">{{order.paid | currency(order.currency)}}</div>
                  </div>
                  <div class="order-totals-info-item" v-if="showRefund(order)">
                    <div class="total-heading">Refunded:</div>
                    <div class="notranslate total-text cancelled">-{{order.refund | currency(order.currency)}}</div>
                  </div>
                  <div class="order-totals-info-item" v-if="showPendingRefund(order)">
                    <div class="total-heading">
                      <b-tooltip target="help" triggers="hover" placement="top">
                        The refund is pending because the payment has not been captured yet.<br/>
                        We will be able to process the refund once the charge is captured.<br/>
                        This may take a few business days to appear.<br/>
                      </b-tooltip>
                      Pending Refund: <img id="help" :src="require('@/assets/images/help.svg')"/>
                    </div>
                    <div class="notranslate total-text pending">-{{order.pending_refund | currency(order.currency)}}</div>
                  </div>
                  <div class="order-totals-info-item" v-if="showChargeback(order)">
                    <div class="total-heading">Chargeback:</div>
                    <div class="notranslate total-text cancelled">-{{order.chargeback | currency(order.currency)}}</div>
                  </div>
                  <hr class="mt-0 order-totals-info-item w-100" />
                  <div class="order-totals-info-item" >
                    <div class="label total-heading fw-500">Total Paid:</div>
                    <div class="notranslate total-text">{{order.total_remaining_balance | currency(order.currency)}}</div>
                  </div>
                  <div class="order-totals-info-item" v-if="showAmountDue(order)">
                    <div class="label total-heading fw-500">Amount Due:</div>
                    <div class="notranslate total-text">{{order.amount_due | currency(order.currency)}}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="centered-block">
              <div class="step-title">No items found</div>
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row class="justify-content-center boxes select-item-btn customer-verify mt-3">
        <b-col v-if="session && session.can_undo && !noback" md="4" lg="3" cols="6" class="back">
          <b-button block @click="goBack()" class="btn-outline-success btn-back stroke" variant="outline-primary">Back</b-button>
        </b-col>
        <b-col v-if="multiple" md="4" lg="3" cols="6">
          <b-button block @click="done()" class="btn btn-default btn-account font-weight-normal select-item-continue-btn" variant="primary" :disabled="!canContinue">{{label}}
            <font-awesome-icon :icon="['fas', 'caret-right']" class="footer_btn"></font-awesome-icon>
          </b-button>
        </b-col>
      </b-row>
  </section>
</template>

<script>
import axios from 'axios'
import {mapState} from 'vuex'
import {
  sortItems, getShortDate, getUTCShortDate, getItemStatus, getItemStatusClass, getItemImageUrl,
  getItemFulfillmentStatus, getItemFulfillmentStatusClass, showSubscription, showFulfillment,
  getShortHumanReadableDate, getPaymentStatus, getPaymentStatusClass, getShortHumanReadableUTCDate,
  isRecurring, setFallbackImageUrl
} from '../utils'
import {BillingCycleType, ItemStatus, InputType} from '../constants'

export default {
  name: 'SelectItems',
  props: {
    input: Object,
    label: String,
    defaults: {
      type: Array,
      default: function() {
        return []
      }
    },
    noback: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    noselect: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    multiple: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    itemSelectLabel: {
      type: String,
      default: function() {
        return 'Select'
      }
    },
    itemSelectIcon: {
      type: String,
      default: function() {
        return 'angle-right'
      }
    }
  },
  data: function() {
    return {
      getShortDate,
      getUTCShortDate,
      orders: [],
      selectedItemMap: {},
      canContinue: false,
      getShortHumanReadableDate,
      getShortHumanReadableUTCDate,
      initialLoading: true
    }
  },
  computed: {
    ...mapState(['session', 'isLoading']),
    activeItemRequired: function() {
      return this.input && this.input.requires_active_item
    },
    isCheckout: function() {
      return this.input && (this.input.type === InputType.CheckoutConfirm)
    }
  },
  methods: {
    done: function() {
      if (this.canContinue) {
        let selectedItems = []

        this.orders.forEach(order => {
          order.items.forEach(item => {
            if (item.id in this.selectedItemMap) {
              selectedItems.push(item)
            }
          })
        })

        this.$emit('select', selectedItems) // done selecting items
      }
    },
    goBack: function() {
      this.$emit('back') // go back to the previous step (ignore changes to item selection)
    },
    showFulfillment: function(item) {
      return showFulfillment(item)
    },
    showSubscription: function(item) {
      return showSubscription(item)
    },
    isRecurring: function(item) {
      return isRecurring(item)
    },
    isSubscriptionActive: function(item) {
      return (item.billing_cycle_type === BillingCycleType.Recurring) && item.can_cancel_subscription
    },
    isSubscriptionPauseScheduled: function(item) {
      return this.isSubscriptionActive(item) && (item.status !== ItemStatus.Paused) && item.pause_resume_date
    },
    isSubscriptionCancelScheduled: function(item) {
      return this.isSubscriptionActive(item) && item.cancel_date
    },
    isSubscriptionComplete: function(item) {
      return item.status === ItemStatus.Complete
    },
    isReplaced: function(item) {
      return item.status === ItemStatus.Replaced
    },
    getPaymentStatus: function(order) {
      return getPaymentStatus(order, false)
    },
    getPaymentStatusClass: function(order) {
      return getPaymentStatusClass(order, false)
    },
    getItemStatus: function(item) {
      return getItemStatus(item, false)
    },
    getItemStatusClass: function(item) {
      return getItemStatusClass(item)
    },
    getItemFulfillmentStatus: function(item) {
      return getItemFulfillmentStatus(item)
    },
    getItemFulfillmentStatusClass: function(item) {
      return getItemFulfillmentStatusClass(item)
    },
    hasPendingCharge: function(item) {
      return parseFloat(item.amount_pending) > Number.EPSILON
    },
    showRefund: function(order) {
      return parseFloat(order.refund) > Number.EPSILON
    },
    showPendingRefund: function(order) {
      return parseFloat(order.pending_refund) > Number.EPSILON
    },
    showChargeback: function(order) {
      return parseFloat(order.chargeback) > Number.EPSILON
    },
    showAmountDue: function(order) {
      return parseFloat(order.amount_due) > Number.EPSILON
    },
    showDiscount: function(order) {
      return parseFloat(order.discount) > Number.EPSILON
    },
    showHiddenFees: function(order) {
      return parseFloat(order.hidden_fees) > Number.EPSILON
    },
    showShipping: function(order) {
      return (parseFloat(order.shipping_price) > Number.EPSILON) || !this.showHiddenFees(order)
    },
    showForwardBalance: function(order) {
      return parseFloat(order.forward_balance) > Number.EPSILON
    },
    showPastBalance: function(order) {
      return parseFloat(order.past_balance) > Number.EPSILON
    },
    toggleItem: function(event, item) {
      event.stopPropagation()
      if (this.multiple) {
        if (item.id in this.selectedItemMap) {
          delete this.selectedItemMap[item.id]
        } else {
          this.selectedItemMap[item.id] = true
        }

        this.updateStatus()
      } else {
        this.$emit('select', [item]) // single selection - done selecting items
      }
    },
    isSelected: function(item) {
      return this.selectedItemMap && !!(item.id in this.selectedItemMap)
    },
    selectAll: function() {
      this.orders.forEach(order => {
        this.selectOrder(order)
      })

      this.updateStatus()
    },
    clearAll: function() {
      this.selectedItemMap = {}
      this.updateStatus()
    },
    selectOrder: function(order) {
      order.items.forEach(item => {
        this.selectedItemMap[item.id] = true
      })

      this.updateStatus()
    },
    clearOrder: function(order) {
      order.items.forEach(item => {
        if (item.id in this.selectedItemMap) {
          delete this.selectedItemMap[item.id]
        }
      })

      this.updateStatus()
    },
    getImageUrl: function(item) {
      return getItemImageUrl(item, 'small')
    },
    setAltImg: function(event, item) {
      setFallbackImageUrl(event, item, 'small', true)
    },
    updateStatus: function() {
      // update the select all checkbox
      let selectionCount = Object.keys(this.selectedItemMap).length
      this.canContinue = selectionCount !== 0

      this.orders = this.orders.slice() // force item statuses to be called
    }
  },
  created() {
    this.$store.commit('busy')
    this.selectedItemMap = {}

    // initialize the set of default selected items
    if (this.multiple) {
      this.defaults.forEach(item => {
        this.selectedItemMap[item.id] = true
      })
    }

    axios.get('/order_items/', {
      params: {
        is_active: this.activeItemRequired,
        checkout: this.isCheckout
      }
    }).then(result => {
      let items = result.data.results
      let lastOrderId = null
      let orders = []
      let order = {}
      let startIndex = 0

      sortItems(items)

      for (let i = 0; i < items.length; i++) {
        let orderId = items[i].order.id

        if (orderId !== lastOrderId) { // if this is the 1st item of a different order
          if (i > 0) {
            // if this is not the 1st item, then copy the items from the previous order
            order['items'] = items.slice(startIndex, i)
            orders.push(order)
          }

          lastOrderId = orderId
          startIndex = i
          order = {
            id: items[i].order.id,
            order_id: items[i].order.order_id,
            created: items[i].order.created,
            subtotal: items[i].order.subtotal,
            shipping_price: items[i].order.shipping_price,
            hidden_fees: items[i].order.hidden_fees,
            tax: items[i].order.tax,
            charged: items[i].order.amount_charged,
            paid: items[i].order.amount_paid,
            discount: items[i].order.discount,
            total: items[i].order.total_discounted_price,
            total_remaining_balance: items[i].order.total_remaining_balance,
            amount_due: items[i].order.amount_due,
            pending_refund: items[i].order.pending_refund_amount,
            refund: items[i].order.settled_refund_amount,
            chargeback: items[i].order.chargeback_amount,
            forward_balance: items[i].order.forward_balance,
            past_balance: items[i].order.past_balance,
            status: items[i].order.status,
            payment_status: items[i].order.payment_status,
            tip: items[i].order.tip,
            currency: items[i].order.currency
          }
        }

        if (i === (items.length - 1)) { // if this is the last item
          order['items'] = items.slice(startIndex)
          orders.push(order)
        }
      }

      this.orders = orders
      this.updateStatus()
      this.$store.commit('ready')

      if (!items.length) {
        this.canContinue = true
      }
      this.initialLoading = false
    }, error => {
      console.error(error)
      this.$store.commit('critical_error', error)
      this.$store.commit('ready')
      this.initialLoading = false
    })
  }
}
</script>

<style scoped>
</style>
